import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { ArrowUp, Check, Copy, FolderPlus, FileText, Globe, Loader2, Mail, MessagesSquare, Paperclip, PanelLeft, SquarePen, Trash2, X } from 'lucide-react';
import { api, csrfToken, type Me } from '../api.ts';
import { ACCEPT, MAX_FILES, prepare, toForm, type Pending } from '../attach.ts';
import { useDashboard, useSave, useTaskDrawer } from '../data.ts';
import { ago } from '../util.ts';
import { Avatar, Err, OpsMark } from '../components/ui.tsx';
import { openOmnibar } from '../components/Omnibar.tsx';

type Source = { title: string; url: string; kind: 'web' | 'drive' | 'gmail' | 'record' };
type Step = { tool: string; label: string };
type Msg = { id: string; role: 'user' | 'model'; text: string; at: string; sources?: Source[]; steps?: Step[]; model?: string; attachments?: { name: string; thumb: string }[] };
type ChatRow = { id: string; title: string; updated_at: string; preview: string };

const STARTERS = [
  { label: 'Where does Rev.B really stand?', text: 'Where does Rev.B really stand right now, and what is the riskiest thing between today and prototypes in hand?' },
  { label: 'FCC Part 15 for Sconce', text: 'Walk me through what FCC Part 15 means for Sconce as a Class B device with a pre-certified radio module. What testing do we actually need and roughly what does it cost?' },
  { label: 'What did we decide lately?', text: 'Summarize the decisions and important updates we logged in the last two weeks.' },
  { label: 'Pressure-test pricing', text: 'Help me pressure-test our pricing approach for Sconce. Ask me what you need to know first.' },
];

// Server-sent events from a POST (EventSource only does GET), parsed by hand.
async function stream(path: string, body: FormData | object, on: (event: string, data: any) => void) {
  const form = body instanceof FormData;
  const res = await fetch(`/api${path}`, { method: 'POST', credentials: 'same-origin', body: form ? body : JSON.stringify(body),
    headers: { 'x-csrf-token': csrfToken(), ...(form ? {} : { 'content-type': 'application/json' }) } });
  if (res.status === 401) { location.href = '/login'; return; }
  if (!res.ok || !res.body) { const j = await res.json().catch(() => ({})); throw new Error(j.error ?? `Request failed (${res.status})`); }
  const reader = res.body.pipeThrough(new TextDecoderStream()).getReader();
  let buf = '';
  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    buf += value;
    let i;
    while ((i = buf.indexOf('\n\n')) >= 0) {
      const block = buf.slice(0, i); buf = buf.slice(i + 2);
      const ev = block.match(/^event: (.*)$/m)?.[1] ?? 'message';
      const data = block.split('\n').filter((l) => l.startsWith('data: ')).map((l) => l.slice(6)).join('\n');
      if (data) on(ev, JSON.parse(data));
    }
  }
}

export function ChatPage({ me }: { me: Me }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [params, setParams] = useSearchParams();
  const list = useQuery({ queryKey: ['chats'], queryFn: () => api<{ chats: ChatRow[] }>('/chats') });
  const thread = useQuery({ queryKey: ['chat', id], queryFn: () => api<{ chat: { id: string; title: string }; messages: Msg[] }>(`/chats/${id}`), enabled: !!id });
  const [msgs, setMsgs] = useState<Msg[]>([]);
  const [live, setLive] = useState<{ text: string; steps: Step[] } | null>(null);
  const [err, setErr] = useState('');
  const [showList, setShowList] = useState(false);
  const busy = !!live;
  const endRef = useRef<HTMLDivElement>(null);
  // While a reply streams into a brand-new conversation, the URL moves to its id; keep the local messages then.
  const streamingInto = useRef<string | null>(null);

  useEffect(() => { if (!id) setMsgs([]); }, [id]);
  useEffect(() => { if (thread.data && streamingInto.current !== id) setMsgs(thread.data.messages); }, [thread.data, id]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' }); }, [msgs.length, live?.text.length, live?.steps.length]);
  useEffect(() => setShowList(false), [id]);

  const send = async (text: string, files: Pending[] = []) => {
    if (busy || (!text.trim() && !files.length)) return;
    setErr('');
    setMsgs((m) => [...m, { id: `local-${Date.now()}`, role: 'user', text, at: new Date().toISOString(), attachments: files.map((f) => ({ name: f.file.name, thumb: f.thumb })) }]);
    setLive({ text: '', steps: [] });
    let chatId = id ?? null;
    try {
      await stream(`/chats/${id ?? 'new'}/messages`, files.length ? toForm({ text }, files) : { text }, (ev, data) => {
        if (ev === 'chat' && !id) { chatId = data.id; streamingInto.current = data.id; navigate(`/chat/${data.id}`, { replace: true }); }
        if (ev === 'step') setLive((l) => l && { ...l, steps: [...l.steps, data] });
        if (ev === 'text') setLive((l) => l && { ...l, text: l.text + data.text });
        if (ev === 'done') setMsgs((m) => [...m, data]);
        if (ev === 'error') setErr(data.error);
      });
    } catch (e) { setErr((e as Error).message); }
    setLive(null);
    streamingInto.current = null;
    qc.invalidateQueries({ queryKey: ['chats'] });
    if (chatId) qc.invalidateQueries({ queryKey: ['chat', chatId] });
  };

  // ⌘K hands a question over as ?q=…: start a new conversation with it.
  useEffect(() => {
    const q = params.get('q');
    if (q && !id) { setParams({}, { replace: true }); send(q); }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const remove = async (cid: string) => {
    if (!confirm('Delete this conversation?')) return;
    await api(`/chats/${cid}`, { method: 'DELETE' });
    qc.invalidateQueries({ queryKey: ['chats'] });
    if (cid === id) navigate('/chat');
  };

  const empty = !id && !msgs.length;
  return (
    <div className={`chat ${showList ? 'list-open' : ''}`}>
      <aside className="chat-list" aria-label="Conversations">
        <Link to="/chat" className="btn primary chat-new"><SquarePen size={14} aria-hidden /> New conversation</Link>
        <ul>
          {(list.data?.chats ?? []).map((c) => (
            <li key={c.id} className={c.id === id ? 'on' : ''}>
              <Link to={`/chat/${c.id}`}><b>{c.title}</b><span className="muted sm">{ago(c.updated_at)}{c.preview ? ` · ${c.preview}` : ''}</span></Link>
              <button className="icon" aria-label={`Delete ${c.title}`} onClick={() => remove(c.id)}><Trash2 size={13} /></button>
            </li>
          ))}
        </ul>
        {list.data && !list.data.chats.length && <p className="muted sm chat-list-empty">Your conversations land here. Only you can see them.</p>}
      </aside>

      <section className="chat-main">
        <header className="chat-h">
          <button className="icon chat-list-toggle" aria-label="Conversations" onClick={() => setShowList(!showList)}><PanelLeft size={18} /></button>
          <h1>{id ? thread.data?.chat.title ?? '…' : 'Chat with Ops'}</h1>
        </header>

        <div className="chat-scroll">
          {empty ? (
            <div className="chat-hello">
              <OpsMark size={34} />
              <h2>What are you working through?</h2>
              <p className="muted">Think out loud, research a vendor or a regulation, or ask about anything at GBS. Ops searches the web, your company's records and your own Drive and Gmail. When something's worth keeping, add it to a project in one tap.</p>
              <div className="starters">{STARTERS.map((s) => <button key={s.label} onClick={() => send(s.text)}>{s.label}</button>)}</div>
            </div>
          ) : (
            <ol className="msgs">
              {msgs.map((m) => <Message key={m.id} m={m} me={me} title={thread.data?.chat.title ?? ''} />)}
              {live && (
                <li className="msg ops">
                  <OpsMark size={22} />
                  <div className="msg-body">
                    {live.steps.length > 0 && <div className="steps">{live.steps.map((s, i) => <span key={i} className="step"><Check size={11} aria-hidden /> {s.label}</span>)}</div>}
                    {live.text ? <Md text={live.text} /> : <p className="thinking"><Loader2 size={14} className="spin" aria-hidden /> {live.steps.length ? 'Reading what it found…' : 'Thinking…'}</p>}
                  </div>
                </li>
              )}
            </ol>
          )}
          {err && <p className="err chat-err">{err}</p>}
          {thread.error && <Err e={thread.error} />}
          <div ref={endRef} />
        </div>

        <Composer onSend={send} busy={busy} autoFocus />
      </section>
    </div>
  );
}

// Links Ops writes as /?task=… open the task drawer instead of leaving the page; everything else opens in a new tab.
function Md({ text }: { text: string }) {
  const { open } = useTaskDrawer();
  return (
    <div className="md">
      <Markdown remarkPlugins={[remarkGfm]} components={{
        a: ({ href, children }) => {
          const task = href?.match(/^\/\?task=([\w-]+)/)?.[1];
          if (task) return <button className="link task-link" onClick={() => open(task)}>{children}</button>;
          return <a href={href} target="_blank" rel="noreferrer">{children}</a>;
        },
        table: ({ children }) => <div className="md-table"><table>{children}</table></div>,
      }}>{text.replace(/<br\s*\/?>/gi, ' · ')}</Markdown>
    </div>
  );
}

const sourceIcon = { web: Globe, drive: FileText, gmail: Mail, record: MessagesSquare };

function Message({ m, me, title }: { m: Msg; me: Me; title: string }) {
  const [copied, setCopied] = useState(false);
  const [adding, setAdding] = useState(false);
  if (m.role === 'user') return (
    <li className="msg you">
      <div className="msg-body">
        {(m.attachments?.length ?? 0) > 0 && <div className="msg-files">{m.attachments!.map((a, i) => <span key={i} className="attach-pill">{a.thumb ? <img src={a.thumb} alt="" /> : <FileText size={14} aria-hidden />}<span className="attach-name">{a.name}</span></span>)}</div>}
        <p>{m.text}</p>
      </div>
      <Avatar email={me.email} people={[{ email: me.email, name: me.name, picture: me.picture ?? '', role: '', active: true, last_login_at: null }]} size={26} />
    </li>
  );
  const host = (u: string) => { try { return new URL(u).hostname.replace(/^www\./, ''); } catch { return ''; } };
  return (
    <li className="msg ops">
      <OpsMark size={22} />
      <div className="msg-body">
        {(m.steps?.length ?? 0) > 0 && <details className="steps-done"><summary className="muted sm">{m.steps!.length} step{m.steps!.length > 1 ? 's' : ''}</summary>
          <div className="steps">{m.steps!.map((s, i) => <span key={i} className="step"><Check size={11} aria-hidden /> {s.label}</span>)}</div></details>}
        <Md text={m.text} />
        {(m.sources?.length ?? 0) > 0 && (
          <div className="sources">{m.sources!.map((s, i) => { const I = sourceIcon[s.kind] ?? Globe; return (
            <a key={i} href={s.url} target="_blank" rel="noreferrer" className="source" title={s.title}><I size={12} aria-hidden /><span>{s.kind === 'web' ? host(s.url) || s.title : s.title}</span></a>
          ); })}</div>
        )}
        <div className="msg-actions">
          <button className="btn ghost sm" onClick={async () => { await navigator.clipboard.writeText(m.text); setCopied(true); setTimeout(() => setCopied(false), 1500); }}>
            {copied ? <Check size={13} aria-hidden /> : <Copy size={13} aria-hidden />} {copied ? 'Copied' : 'Copy'}</button>
          <button className="btn ghost sm" onClick={() => setAdding(!adding)}><FolderPlus size={13} aria-hidden /> Add to project</button>
          <button className="btn ghost sm" onClick={() => openOmnibar(`From a Chat with Ops:\n\n${m.text.slice(0, 3000)}`)}><OpsMark size={11} /> Log as an update</button>
          {m.model && <span className="model-badge" title="The Gemini model Ops used">powered by {m.model}</span>}
        </div>
        {adding && <AddToProject m={m} me={me} title={title} onDone={() => setAdding(false)} />}
      </div>
    </li>
  );
}

function Composer({ onSend, busy, autoFocus }: { onSend: (text: string, files: Pending[]) => void; busy: boolean; autoFocus?: boolean }) {
  const [text, setText] = useState('');
  const [files, setFiles] = useState<Pending[]>([]);
  const [fileErr, setFileErr] = useState('');
  const ref = useRef<HTMLTextAreaElement>(null);
  const picker = useRef<HTMLInputElement>(null);
  useEffect(() => { if (autoFocus && matchMedia('(min-width: 801px)').matches) ref.current?.focus(); }, [autoFocus]);
  useEffect(() => { const el = ref.current; if (el) { el.style.height = 'auto'; el.style.height = `${Math.min(el.scrollHeight, 220)}px`; } }, [text]);
  const add = async (list: File[]) => {
    const room = MAX_FILES - files.length;
    const ready = await Promise.all(list.slice(0, Math.max(0, room)).map(prepare));
    const bad = ready.filter((p) => p.error);
    setFileErr([list.length > room ? `Up to ${MAX_FILES} attachments.` : '', ...bad.map((b) => `${b.file.name}: ${b.error}`)].filter(Boolean).join(' '));
    setFiles((cur) => [...cur, ...ready.filter((p) => !p.error)].slice(0, MAX_FILES));
  };
  const go = () => { if (busy || (!text.trim() && !files.length)) return; onSend(text.trim(), files); setText(''); setFiles([]); };
  return (
    <form className="composer" onSubmit={(e) => { e.preventDefault(); go(); }}
      onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); add([...e.dataTransfer.files]); }}>
      {files.length > 0 && <div className="attach-row">{files.map((f) => (
        <span key={f.id} className="attach-pill">{f.thumb ? <img src={f.thumb} alt="" /> : <FileText size={16} aria-hidden />}<span className="attach-name">{f.file.name}</span>
          <button type="button" className="icon" aria-label={`Remove ${f.file.name}`} onClick={() => setFiles((c) => c.filter((x) => x.id !== f.id))}><X size={12} /></button></span>
      ))}</div>}
      <div className="composer-row">
        <button type="button" className="icon" aria-label="Attach a screenshot or PDF" onClick={() => picker.current?.click()} disabled={files.length >= MAX_FILES}><Paperclip size={17} /></button>
        <input ref={picker} type="file" accept={ACCEPT.join(',')} multiple hidden onChange={(e) => { add([...(e.target.files ?? [])]); e.target.value = ''; }} />
        <textarea ref={ref} rows={1} value={text} onChange={(e) => setText(e.target.value)} placeholder="Message Ops…" aria-label="Message"
          onPaste={(e) => { const f = [...e.clipboardData.files]; if (f.length) { e.preventDefault(); add(f); } }}
          onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) { e.preventDefault(); go(); } }} />
        <button className="send" disabled={busy || (!text.trim() && !files.length)} aria-label="Send">{busy ? <Loader2 size={16} className="spin" /> : <ArrowUp size={17} />}</button>
      </div>
      {fileErr && <p className="err sm">{fileErr}</p>}
      <p className="composer-note muted">Private to you · searches the web, GBS records, your Drive and Gmail · add what you find to a project from any answer</p>
    </form>
  );
}

// Research that should live on: a task in a project, carrying the answer and its sources, so it shows up where the work is.
function AddToProject({ m, me, title, onDone }: { m: Msg; me: Me; title: string; onDone: () => void }) {
  const d = useDashboard();
  const save = useSave();
  const { open } = useTaskDrawer();
  const projects = d.data?.projects ?? [];
  const [project, setProject] = useState(() => { try { return localStorage.getItem('gbs-chat-project') ?? ''; } catch { return ''; } });
  const [name, setName] = useState(title.slice(0, 120));
  const [mine, setMine] = useState(true);
  const [made, setMade] = useState<{ id: string; key: string } | null>(null);
  const pid = project && projects.some((p) => p.id === project) ? project : projects[0]?.id ?? '';
  if (made) return <p className="add-done sm"><Check size={13} aria-hidden /> Added to {made.key}. <button className="link" onClick={() => open(made.id)}>Open the task</button> · <button className="link" onClick={onDone}>Done</button></p>;
  const details = `${m.text}${m.sources?.length ? `\n\nSources:\n${m.sources.map((x) => `- ${x.title}: ${x.url}`).join('\n')}` : ''}\n\n(From a Chat with Ops, ${new Date(m.at).toLocaleDateString()})`;
  return (
    <form className="add-proj" onSubmit={async (e) => {
      e.preventDefault();
      const r = await save.mutateAsync({ path: '/tasks', body: { title: name.trim(), project: pid, details: details.slice(0, 20000), owner: mine ? me.email : null } }) as { task: { id: string } };
      try { localStorage.setItem('gbs-chat-project', pid); } catch { /* per-browser convenience only */ }
      setMade({ id: r.task.id, key: projects.find((p) => p.id === pid)?.key ?? '' });
    }}>
      <select value={pid} onChange={(e) => setProject(e.target.value)} aria-label="Project">{projects.map((p) => <option key={p.id} value={p.id}>{p.key} · {p.title}</option>)}</select>
      <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Task name" aria-label="Task name" required maxLength={300} />
      <label className="sm row"><input type="checkbox" checked={mine} onChange={(e) => setMine(e.target.checked)} /> Assign to me</label>
      <div className="row"><button className="btn primary sm" disabled={!name.trim() || !pid || save.isPending}>Add task</button><button type="button" className="btn ghost sm" onClick={onDone}>Cancel</button></div>
      <p className="muted sm">The answer and its sources go into the task's details.</p>
      <Err e={save.error} />
    </form>
  );
}
